/**
 * <s-shipping-calc> — CEP shipping calculator in the cart (snippets/shipping-calculator.liquid).
 * Uses the cart shipping-rates endpoints (prepare, then poll the async result), so the
 * quotes are the live carrier-app rates (Frenet) for the current cart. The state (UF) is
 * derived from the CEP range because Brazilian addresses need a province.
 * The CEP is remembered for the session and re-quoted when the cart footer re-renders.
 */
const STORAGE_KEY = 'sign:cep';
const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** [first 5 CEP digits from, to, UF code, Shopify province name] */
const CEP_RANGES = [
  [1000, 19999, 'SP', 'São Paulo'], [20000, 28999, 'RJ', 'Rio de Janeiro'], [29000, 29999, 'ES', 'Espírito Santo'],
  [30000, 39999, 'MG', 'Minas Gerais'], [40000, 48999, 'BA', 'Bahia'], [49000, 49999, 'SE', 'Sergipe'],
  [50000, 56999, 'PE', 'Pernambuco'], [57000, 57999, 'AL', 'Alagoas'], [58000, 58999, 'PB', 'Paraíba'],
  [59000, 59999, 'RN', 'Rio Grande do Norte'], [60000, 63999, 'CE', 'Ceará'], [64000, 64999, 'PI', 'Piauí'],
  [65000, 65999, 'MA', 'Maranhão'], [66000, 68899, 'PA', 'Pará'], [68900, 68999, 'AP', 'Amapá'],
  [69000, 69299, 'AM', 'Amazonas'], [69300, 69399, 'RR', 'Roraima'], [69400, 69899, 'AM', 'Amazonas'],
  [69900, 69999, 'AC', 'Acre'], [70000, 72799, 'DF', 'Distrito Federal'], [72800, 72999, 'GO', 'Goiás'],
  [73000, 73699, 'DF', 'Distrito Federal'], [73700, 76799, 'GO', 'Goiás'], [76800, 76999, 'RO', 'Rondônia'],
  [77000, 77999, 'TO', 'Tocantins'], [78000, 78899, 'MT', 'Mato Grosso'], [79000, 79999, 'MS', 'Mato Grosso do Sul'],
  [80000, 87999, 'PR', 'Paraná'], [88000, 89999, 'SC', 'Santa Catarina'], [90000, 99999, 'RS', 'Rio Grande do Sul'],
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class SShippingCalc extends HTMLElement {
  #busy = false;
  #deadline = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeydown);
    const saved = sessionStorage.getItem(STORAGE_KEY) || '';
    const input = this.#input;
    if (saved && input) {
      input.value = this.#format(saved);
      this.calculate();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  get #input() {
    const input = this.querySelector('[data-shipping-cep]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #format(digits) {
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5, 8)}` : digits;
  }

  #onInput = (event) => {
    if (event.target !== this.#input || !this.#input) return;
    this.#input.value = this.#format(this.#input.value.replace(/\D/g, '').slice(0, 8));
  };

  #onKeydown = (event) => {
    // Enter must not submit the cart form (that would go to checkout).
    if (event.key === 'Enter' && event.target === this.#input) {
      event.preventDefault();
      this.calculate();
    }
  };

  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-shipping-submit]')) this.calculate();
  };

  #showError(message) {
    const error = this.querySelector('[data-shipping-error]');
    if (error instanceof HTMLElement) {
      error.textContent = message;
      error.hidden = !message;
    }
  }

  async calculate() {
    if (this.#busy) return;
    const digits = (this.#input?.value || '').replace(/\D/g, '');
    const prefix = Number(digits.slice(0, 5));
    const range = digits.length === 8 ? CEP_RANGES.find(([from, to]) => prefix >= from && prefix <= to) : null;
    const results = this.querySelector('[data-shipping-results]');
    if (results) results.innerHTML = '';
    if (!range) {
      this.#showError(this.dataset.msgInvalid || '');
      return;
    }
    this.#showError('');
    sessionStorage.setItem(STORAGE_KEY, digits);

    const button = this.querySelector('[data-shipping-submit]');
    this.#busy = true;
    button?.setAttribute('aria-busy', 'true');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.dataset.label = button.textContent?.trim() || '';
      button.innerHTML = `<span class="inline-flex items-center gap-2"><span class="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden="true"></span>${this.dataset.labelLoading || ''}</span>`;
    }
    if (results) {
      const loading = document.createElement('li');
      loading.className = 'flex flex-col gap-1.5';
      loading.setAttribute('data-shipping-loading', '');
      loading.innerHTML = `<span class="text-xs text-on-surface-secondary">${this.dataset.msgLoading || ''}</span><span class="h-11 animate-pulse rounded-md bg-border/60"></span><span class="h-11 animate-pulse rounded-md bg-border/40"></span>`;
      results.append(loading);
    }
    this.#deadline = Date.now() + 20000;
    try {
      let rates = await this.#quote(digits, range[3]);
      if (rates === 'retry') rates = await this.#quote(digits, range[2]);
      if (!Array.isArray(rates)) throw new Error(this.dataset.msgFailed || '');
      if (!rates.length) this.#showError(this.dataset.msgNone || '');
      else this.#render(rates);
    } catch (error) {
      const aborted = error instanceof Error && error.name === 'AbortError';
      this.#showError((!aborted && error instanceof Error && error.message) || this.dataset.msgFailed || '');
    } finally {
      this.#busy = false;
      button?.removeAttribute('aria-busy');
      results?.querySelector('[data-shipping-loading]')?.remove();
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.textContent = button.dataset.label || '';
      }
    }
  }

  /** Aborts a request when the overall 20 s quote deadline passes (no endless spinner). */
  #timeout() {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), Math.max(1000, this.#deadline - Date.now()));
    return controller.signal;
  }

  /** Returns the rates array, 'retry' when the province was rejected, or throws. */
  async #quote(zip, province) {
    const params = new URLSearchParams({
      'shipping_address[zip]': `${zip.slice(0, 5)}-${zip.slice(5)}`,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': province,
    });
    const prepare = await fetch(`${this.dataset.prepareUrl}?${params}`, { method: 'POST', headers: { Accept: 'application/json' }, signal: this.#timeout() });
    if (prepare.status === 422) {
      const body = await prepare.json().catch(() => ({}));
      if (body?.province) return 'retry';
      if (body?.zip) throw new Error(this.dataset.msgInvalid || '');
      throw new Error(this.dataset.msgFailed || '');
    }
    while (Date.now() < this.#deadline) {
      const response = await fetch(`${this.dataset.ratesUrl}?${params}`, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: this.#timeout() });
      if (response.status === 422) {
        const body = await response.json().catch(() => ({}));
        if (body?.province) return 'retry';
        throw new Error(body?.zip ? this.dataset.msgInvalid || '' : this.dataset.msgFailed || '');
      }
      const body = response.ok ? await response.json().catch(() => null) : null;
      if (body && Array.isArray(body.shipping_rates)) return body.shipping_rates;
      await sleep(600);
    }
    throw new Error(this.dataset.msgFailed || '');
  }

  #render(rates) {
    const results = this.querySelector('[data-shipping-results]');
    if (!results) return;
    const reached = Number(this.dataset.total) >= Number(this.dataset.threshold);
    const freeMax = Number(this.dataset.freeMax) || 0;
    const sorted = [...rates].sort((a, b) => Number(a.price) - Number(b.price));
    for (const rate of sorted) {
      const cents = Math.round(Number(rate.price) * 100);
      const free = reached && cents <= freeMax;

      const item = document.createElement('li');
      item.className = 'flex min-w-0 items-start justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2 text-sm';
      const info = document.createElement('span');
      info.className = 'min-w-0 flex-1 break-words';
      const name = document.createElement('span');
      name.className = 'block font-medium text-on-surface';
      name.textContent = rate.name || '';
      info.append(name);
      const days = Array.isArray(rate.delivery_days) ? rate.delivery_days.filter((d) => d != null) : [];
      if (days.length && !/\d/.test(rate.name || '')) {
        const max = Math.max(...days.map(Number));
        const eta = document.createElement('span');
        eta.className = 'block text-xs text-on-surface-secondary';
        eta.textContent = (this.dataset.labelDays || '').replace('%DAYS%', String(max));
        info.append(eta);
      }
      const price = document.createElement('span');
      price.className = 'shrink-0 text-end';
      if (free || cents === 0) {
        price.innerHTML = '';
        const label = document.createElement('strong');
        label.className = 'block font-semibold text-[#1f5c3a]';
        label.textContent = this.dataset.labelFree || '';
        price.append(label);
        if (cents > 0) {
          const old = document.createElement('s');
          old.className = 'block text-xs text-on-surface-secondary';
          old.textContent = money.format(cents / 100);
          price.append(old);
        }
      } else {
        price.className += ' font-medium text-on-surface';
        price.textContent = money.format(cents / 100);
      }
      item.append(info, price);
      results.append(item);
    }
  }
}

if (!customElements.get('s-shipping-calc')) customElements.define('s-shipping-calc', SShippingCalc);
